import React, { memo, useEffect } from 'react';
import { Button } from 'reactstrap';
// Constants
import { PROGRAM_TYPES } from 'site-modules/shared/constants/incentives';
import { TrackingConstant } from 'client/tracking/constant';
import { ZIP_CODE_QUESTION_ID } from 'site-modules/shared/components/incentives/incentives-wizard/questions/questions';
import { wizardTrackingIds } from 'client/tracking/maps/incentives-wizard';
import { TRACKING_BY_GRANTOR } from 'site-modules/shared/components/incentives/incentives-wizard/constants/tracking';
// Utils
import { getPlural } from 'client/utils/plural';
import { EventToolbox } from 'client/utils/event-toolbox';
import { getTotalRebateAmount } from 'site-modules/shared/utils/incentives/get-rebate-amount-from-incentives';
import { getIncentiveIdEligibilityFactorIdPairs } from 'site-modules/shared/components/incentives/incentives-wizard/utils/get-incentive-id-eligibility-factor-id-pairs';
import { isTreehousePromoIncentive } from 'site-modules/shared/components/incentives/incentives-wizard/utils/is-treehouse-promo-incentive';
import { isOEMRebate } from 'site-modules/shared/components/incentives/incentives-wizard/utils/is-oem-rebate';
// Components
import { EligibilityIncentivesList } from 'site-modules/shared/components/incentives/incentives-wizard/steps/screens/components/eligibility-incentives-list';
import { WizardPrice } from 'site-modules/shared/components/incentives/incentives-wizard/wizard-price';
import {
  StepProps,
  StepPropsDefaults,
} from 'site-modules/shared/components/incentives/incentives-wizard/prop-types/step-props';
import { WizardImage } from 'site-modules/shared/components/incentives/incentives-wizard/steps/screens/components/wizard-image';
import { NextSteps } from 'site-modules/shared/components/incentives/incentives-wizard/steps/screens/components/next-steps';
import { EmailReviewAnswersSection } from 'site-modules/shared/components/incentives/incentives-wizard/steps/screens/components/email-review-answers-section';
import { ResultScreenCta } from 'site-modules/shared/components/incentives/incentives-wizard/steps/screens/components/result-screen-cta';
import { buildEligibleIncentives } from 'site-modules/shared/components/incentives/incentives-wizard/utils/build-eligible-incentives';
import { EmailReviewAnswersCta } from 'site-modules/shared/components/incentives/incentives-wizard/steps/screens/components/email-review-answers-cta';

import './result-screen.scss';

export const ResultScreen = memo(
  ({
    stepConfig: { srpLink },
    onFinish = StepPropsDefaults.onFinish,
    context: { make, model, bodyType } = StepPropsDefaults.context,
    allIncentives,
    eligibleIncentiveIds = StepPropsDefaults.eligibleIncentiveIds,
    onExit,
    creativeId = StepPropsDefaults.creativeId,
    answers = StepPropsDefaults.answers,
    onStartConvexAnimation,
    flowSkipped = StepPropsDefaults.flowSkipped,
    onStartOver = StepPropsDefaults.onStartOver,
  }) => {
    const eligibleIncentives = buildEligibleIncentives(allIncentives, eligibleIncentiveIds);

    const incentiveIdEligibilityFactorIdPairs = getIncentiveIdEligibilityFactorIdPairs(eligibleIncentives);

    const eligibleSubtypeIds = eligibleIncentives.map(({ subtypeId }) => subtypeId);
    const allEligibleIncentiveIds = eligibleIncentives.map(({ id }) => id);

    const total = getTotalRebateAmount(eligibleIncentives);

    const ineligibleIncentives = allIncentives.filter(
      ({ id, programType, primary, compatibleIncentiveIds }) =>
        !eligibleIncentives.find(({ id: incId }) => incId === id) &&
        (Object.values(PROGRAM_TYPES).includes(programType) ||
          isOEMRebate({ programType, primary, compatibleIncentiveIds }))
    );
    const rebatesNumber = eligibleIncentives.length;

    useEffect(
      () => {
        if (!flowSkipped) {
          onFinish(total);
        }
        onStartConvexAnimation(true);

        EventToolbox.fireTrackAction({
          event_type: TrackingConstant.EVENT_TYPE_ACTION_END,
          event_data: {
            action_name: TrackingConstant.ACTION_FILL_FORM,
            subaction_name: TrackingConstant.MATCHED_INCENTIVES_COUNT,
            action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
            action_category: TrackingConstant.USER_ACTION_CATEGORY,
            creative_id: creativeId,
            value: rebatesNumber,
          },
        });
        EventToolbox.fireTrackAction({
          event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
          event_data: {
            action_name: TrackingConstant.ACTION_FILL_FORM,
            subaction_name: TrackingConstant.MATCHED_INCENTIVES_SUM,
            action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
            action_category: TrackingConstant.USER_ACTION_CATEGORY,
            creative_id: creativeId,
            value: total,
          },
        });

        if (eligibleIncentives.some(isTreehousePromoIncentive)) {
          EventToolbox.fireTrackAction({
            event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
            event_data: {
              action_name: TrackingConstant.ACTION_WIDGET_VIEW,
              action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
              creative_id: TRACKING_BY_GRANTOR.Treehouse.creativeId,
            },
          });
        }

        return () => {
          onStartConvexAnimation(false);
        };
      },

      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    const zip = answers[ZIP_CODE_QUESTION_ID]?.[0];

    return (
      <div className="result-screen">
        <div className="rounded-16 p-1_5">
          <div className="pos-r pt-3">
            <WizardImage bodyType={bodyType} />
            {!!flowSkipped && (
              <div className="determine-msg d-flex bg-green-100 rounded-16 text-cool-gray-30 medium mb-1_5 mx-md-1_5 p-0_75 p-md-1">
                <i aria-hidden className="icon icon-checkmark-circle text-green-50 me-0_5" />
                <div className="d-flex flex-column flex-md-row">
                  <div className="d-flex flex-column d-md-inline">
                    {!!rebatesNumber && <span className="fw-bold">You’re all set! </span>}
                    <span>Results have been determined using previously provided data.</span>{' '}
                  </div>
                  <Button
                    onClick={onStartOver}
                    className="text-transform-none text-cool-gray-30 text-start bg-transparent border-0 p-0 fw-medium ms-md-0_75 mt-0_25 mt-md-0"
                    data-tracking-id={wizardTrackingIds.openIncentivesMatcher}
                    data-tracking-value="Start over"
                  >
                    <span className="text-underline">Start over?</span>
                  </Button>
                </div>
              </div>
            )}
            <div className="result-info mx-auto text-center">
              <h2 className="size-24 fw-bold mb-1_5">
                {rebatesNumber
                  ? `Nice! We found ${rebatesNumber} ${getPlural('rebate', rebatesNumber)} that you’re eligible for.`
                  : 'Unfortunately, we didn’t find any eligible rebates.'}
              </h2>
              {rebatesNumber ? (
                <div className="p-1_5 rounded-12 bg-blue-100 text-center">
                  <>
                    <div className="fw-bold">You’re saving</div>
                    <WizardPrice
                      value={total}
                      className="fw-bold size-36"
                      odometerWrapperClassName="odometer-wrapper"
                    />
                    <div className="text-cool-gray-10 medium">
                      on your {make} {model}
                    </div>
                    {!!rebatesNumber && !!zip && (
                      <EmailReviewAnswersSection
                        make={make}
                        model={model}
                        eligibleSubtypeIds={eligibleSubtypeIds}
                        eligibleIncentiveIds={allEligibleIncentiveIds}
                        incentiveIdEligibilityFactorIdPairs={incentiveIdEligibilityFactorIdPairs}
                        zip={zip}
                        creativeId={creativeId}
                        onStartOver={onStartOver}
                      />
                    )}
                  </>
                </div>
              ) : (
                <>
                  <ResultScreenCta onExit={onExit} srpLink={srpLink}>
                    Keep Shopping
                  </ResultScreenCta>
                  <div className="my-1">
                    <EmailReviewAnswersCta
                      onClick={onStartOver}
                      className="fw-normal"
                      icon={<i className="icon-reset me-0_5 size-12" aria-hidden />}
                      data-tracking-id={wizardTrackingIds.openIncentivesMatcher}
                      data-tracking-value="Start over"
                    >
                      Start over
                    </EmailReviewAnswersCta>
                  </div>
                </>
              )}
            </div>

            <EligibilityIncentivesList
              eligibleIncentives={eligibleIncentives}
              ineligibleIncentives={ineligibleIncentives}
            />
          </div>
        </div>
        <NextSteps make={make} model={model} srpLink={srpLink} onExit={onExit} withSteps={!!rebatesNumber} />
      </div>
    );
  }
);

ResultScreen.displayName = 'ResultScreen';

ResultScreen.propTypes = StepProps;
